import { Activity, useEffect, useState } from 'react'
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  FormControl,
  FormHelperText,
  IconButton,
  InputLabel,
  MenuItem,
  Paper,
  Select,
  Stack,
  TextField,
  Typography,
} from '@mui/material'
import { Link, useNavigate, useParams } from 'react-router-dom'

import ArrowBackIcon from '@mui/icons-material/ArrowBack'
import fetchWithAuth from '../../utilities/fetchWithAuth'
import { usePageTitle } from '../../context/PageTitleContext'

const EditDataTypePage = () => {
  const { uuid } = useParams()
  const { setBreadcrumbs } = usePageTitle()
  const [dataTypeData, setDataTypeData] = useState({
    name: '',
    subtype: 'file',
  })
  const [errors, setErrors] = useState({})
  const [loading, setLoading] = useState(false)
  const [fetching, setFetching] = useState(true)
  const [error, setError] = useState(null)
  const [success, setSuccess] = useState(false)

  const navigate = useNavigate()

  useEffect(() => {
    const fetchDataType = async () => {
      try {
        setFetching(true)
        const { data } = await fetchWithAuth(`data-types/${uuid}`)
        setDataTypeData({
          name: data.name || '',
          subtype: data.subtype || '',
        })
        setBreadcrumbs(['data-types', data.name, 'edit'])
      } catch (error) {
        console.error('Error fetching data type:', error)
        setError('Failed to load data type. Please try again.')
      } finally {
        setFetching(false)
      }
    }

    fetchDataType()
    return () => setBreadcrumbs([])
  }, [uuid])

  const validate = () => {
    const newErrors = {}

    if (!dataTypeData.name.trim()) {
      newErrors.name = 'Name is required'
    }

    if (!dataTypeData.subtype.trim()) {
      newErrors.subtype = 'Subtype is required'
    }

    setErrors(newErrors)
    return Object.keys(newErrors).length === 0
  }

  const handleChange = (e) => {
    setDataTypeData({
      ...dataTypeData,
      [e.target.name]: e.target.value,
    })

    if (errors[e.target.name]) {
      setErrors({
        ...errors,
        [e.target.name]: undefined,
      })
    }
  }

  const handleSubmit = async (e) => {
    e.preventDefault()

    if (!validate()) {
      return
    }

    setLoading(true)
    setError(null)
    setSuccess(false)

    try {
      const response = await fetchWithAuth(`data-types/${uuid}`, {
        method: 'PUT',
        headers: {
          'Content-Type': 'application/json',
        },
        body: JSON.stringify(dataTypeData),
      })

      if (response.status === 200) {
        setSuccess(true)
        setTimeout(() => {
          navigate(`/data-types/${uuid}`)
        }, 1500)
      } else {
        const data = await response.json()
        if (data.errors) {
          const apiErrors = {}
          data.errors.forEach((err) => {
            apiErrors[err.field] = err.message
          })
          setErrors(apiErrors)
        } else {
          setError(data.message || 'Failed to update data type')
        }
      }
    } catch (error) {
      console.error('Error updating data type:', error)
      setError('Failed to update data type. Please try again.')
    } finally {
      setLoading(false)
    }
  }

  if (fetching) {
    return (
      <Box sx={{ p: 3, textAlign: 'center' }}>
        <CircularProgress />
        <Typography variant="h6" sx={{ mt: 2 }}>
          Loading data type...
        </Typography>
      </Box>
    )
  }

  return (
    <Box sx={{ p: 3 }}>
      <Stack direction="row" alignItems="center" spacing={1} sx={{ mb: 3 }}>
        <IconButton component={Link} to={`/data-types/${uuid}`} edge="start">
          <ArrowBackIcon />
        </IconButton>
        <Typography variant="h5">Edit Data Type</Typography>
      </Stack>

      <Activity mode={error ? 'visible' : 'hidden'}>
        <Alert severity="error" sx={{ mb: 3 }}>
          {error}
        </Alert>
      </Activity>

      <Activity mode={success ? 'visible' : 'hidden'}>
        <Alert severity="success" sx={{ mb: 3 }}>
          Data type updated successfully! Redirecting...
        </Alert>
      </Activity>

      <Paper sx={{ p: 3 }}>
        <form onSubmit={handleSubmit}>
          <Stack spacing={3}>
            <TextField
              label="Name"
              name="name"
              value={dataTypeData.name}
              onChange={handleChange}
              fullWidth
              required
              error={!!errors.name}
              helperText={errors.name}
            />

            <FormControl fullWidth required error={!!errors.subtype}>
              <InputLabel id="subtype-label" htmlFor="subtype">
                Subtype
              </InputLabel>
              <Select
                labelId="subtype-label"
                name="subtype"
                value={dataTypeData.subtype}
                onChange={handleChange}
                label="Subtype"
                inputProps={{ id: 'subtype' }}
                disabled
              >
                <MenuItem value="file">File</MenuItem>
                <MenuItem value="analysis">Analysis</MenuItem>
              </Select>
              <Activity mode={errors.subtype ? 'visible' : 'hidden'}>
                <FormHelperText>{errors.subtype}</FormHelperText>
              </Activity>
            </FormControl>

            <Box sx={{ display: 'flex', gap: 2, mt: 3 }}>
              <Button
                variant="contained"
                color="primary"
                type="submit"
                disabled={loading}
                startIcon={
                  loading ? (
                    <CircularProgress size={20} color="inherit" />
                  ) : null
                }
              >
                {loading ? 'Saving...' : 'Save Changes'}
              </Button>
              <Button
                variant="outlined"
                component={Link}
                to={`/data-types/${uuid}`}
                disabled={loading}
              >
                Cancel
              </Button>
            </Box>
          </Stack>
        </form>
      </Paper>
    </Box>
  )
}

export default EditDataTypePage
